import type { PriceCheckerDevice, PriceCheckerRegistration } from '@pos/types';
import {
  type ApiErrorEnvelope,
  type ApiSuccessEnvelope,
  type AuditCategory,
  type AuditLogEntry,
  type AuthLoginRequest,
  type AuthLoginResponse,
  type BackupListResponse,
  type BranchManualPaymentChannel,
  type CreateDeviceRecoveryCodeRequest,
  type CreateDeviceRecoveryCodeResponse,
  type CreateEnrollmentResponse,
  type DashboardEvidenceVerificationReport,
  type DashboardInventoryReport,
  type DashboardOverview,
  type DashboardProductListQuery,
  type DashboardProductListResponse,
  type DashboardProductSalesDetailReport,
  type DashboardReceiptDetailResponse,
  type DashboardReceiptListResponse,
  type DashboardSalesReport,
  type DashboardShiftReport,
  type DashboardTaxInvoiceCustomerSearchResponse,
  type DashboardTaxInvoiceIssueResponse,
  type DashboardTransfer,
  type DashboardTransferCreateInput,
  type DashboardTransferReceiveInput,
  type DashboardWholesaleSale,
  type DashboardWholesaleSaleCreateInput,
  type DashboardZReport,
  DEFAULT_SYSTEM_SETTINGS,
  type DeviceHealthQueryResponse,
  type DeviceManagementRecord,
  isReservedProductBarcode,
  type ManualPaymentChannel,
  type MasterDataEntity,
  type MasterDataPullResponse,
  type MasterDataVersionEnvelope,
  type MasterDataWriteRequest,
  type Promotion as SharedPromotion,
  type SyncHealthReport,
  type SystemSettings as SharedSystemSettings,
  type TaxInvoiceCustomerInput,
  type TokenRefreshResponse,
} from '@pos/types';

import {
  type BoUser,
  canEditMasterData,
  canEditPromotions,
  canEditSystemConfig,
} from '../lib/permissions';
import { appendPermissionDeniedAudit } from '../lib/securityAudit';
import { useSession } from '../stores/session';
import { useUi } from '../stores/ui';
import type {
  Branch,
  BranchPaymentSettings,
  BranchPricing,
  Category,
  PaymentMethodConfig,
  Product,
  ProductUnit,
  Promotion,
  StockMovement,
  StockRow,
  SystemSettings,
  User,
} from '../types/domain';

export class DashboardApiError extends Error {
  readonly code: string;
  readonly status: number;
  readonly details?: Record<string, unknown>;
  readonly requestId?: string;
  readonly endpoint?: string;

  constructor(code: string, message: string);
  constructor(input: {
    code: string;
    message: string;
    status: number;
    details?: Record<string, unknown>;
    requestId?: string;
    endpoint?: string;
  });
  constructor(
    input:
      | string
      | {
          code: string;
          message: string;
          status: number;
          details?: Record<string, unknown>;
          requestId?: string;
          endpoint?: string;
        },
    message?: string,
  ) {
    const normalized =
      typeof input === 'string'
        ? { code: input, message: message ?? '', status: 0 }
        : input;
    const normalizedMessage = normalized.message.startsWith(
      `[${normalized.code}]`,
    )
      ? normalized.message
      : `[${normalized.code}] ${normalized.message}`;
    super(normalizedMessage);
    this.name = 'DashboardApiError';
    this.code = normalized.code;
    this.status = normalized.status;
    this.details = normalized.details;
    this.requestId =
      normalized.requestId ??
      (typeof normalized.details?.requestId === 'string'
        ? normalized.details.requestId
        : undefined);
    this.endpoint = normalized.endpoint;
  }
}

export interface DashboardApiContext {
  tenantId: string;
  branchId: string;
  actorUserId: string;
  affectedDeviceIds: string[];
}

export type DashboardReconciliationStatus =
  | 'late_payment'
  | 'conflict'
  | 'mismatch'
  | 'unsettled'
  | 'pending'
  | 'matched'
  | 'rejected'
  | 'voided'
  | 'missing';

export interface DashboardReconciliationSummary {
  readonly total: number;
  readonly late_payment: number;
  readonly conflict: number;
  readonly mismatch: number;
  readonly unsettled: number;
  readonly pending: number;
  readonly matched: number;
  readonly rejected: number;
  readonly voided: number;
  readonly missing: number;
}

export interface DashboardReconciliationPayment {
  readonly paymentId: string;
  readonly branchId: string;
  readonly saleId: string;
  readonly provider: string;
  readonly environment: string;
  readonly amountSatang: number;
  readonly currency: string;
  readonly status: DashboardReconciliationStatus;
  readonly gatewayTruth: string;
  readonly projectionTruth: string;
  readonly latestAttemptStatus: string | null;
  readonly openAlertCount: number;
  readonly createdAt: string;
  readonly updatedAt: string;
}

export interface DashboardGatewayProbeInput {
  readonly paymentId: string;
  readonly branchId: string;
}

/** Read-only provider status probe. It never changes payment status. */
export interface DashboardGatewayProbe {
  readonly attemptId: string;
  readonly attemptStatus: string;
  readonly provider: string;
  readonly environment: string;
  readonly reference: string;
  readonly probedAt: string;
  readonly observation:
    | {
        readonly kind: 'observed';
        readonly status: string;
        readonly providerTransactionId?: string;
      }
    | { readonly kind: 'provider_error'; readonly code: string };
}

export interface DashboardReconciliationListInput {
  readonly from: string;
  readonly to: string;
  readonly branchId?: string;
  readonly status?: DashboardReconciliationStatus;
  readonly limit?: number;
  readonly cursor?: string;
}

export interface DashboardReconciliationListResponse {
  readonly summary: DashboardReconciliationSummary;
  readonly payments: readonly DashboardReconciliationPayment[];
  readonly nextCursor: string | null;
}

export interface DashboardReconciliationAttempt {
  readonly attemptId: string | null;
  readonly status: string | null;
  readonly providerTransactionId: string | null;
  readonly references: {
    readonly primary: string | null;
    readonly reconciliation: string | null;
    readonly routing: string | null;
  } | null;
  readonly expiresAt: string | null;
  readonly lastStatusCheckedAt: string | null;
  readonly reconciliationSuppressedAt: string | null;
  readonly reconciliationSuppressionCode: string | null;
  readonly createdAt: string | null;
  readonly updatedAt: string | null;
}

export interface DashboardReconciliationEvent {
  readonly eventId: string | null;
  readonly attemptId: string | null;
  readonly source: string | null;
  readonly validationResult: string | null;
  readonly processingResult: string | null;
  readonly authenticationProvenance: string | null;
  readonly verificationErrorCode: string | null;
  readonly occurredAt: string | null;
  readonly receivedAt: string | null;
}

export interface DashboardReconciliationSettlement {
  readonly matchId: string | null;
  readonly settlementRowId: string | null;
  readonly providerTransactionId: string | null;
  readonly matchedAmountSatang: number | null;
  readonly settlementAmountSatang: number;
  readonly currency: string | null;
  readonly outcome: string | null;
  readonly matchVersion: number;
  readonly settledAt: string | null;
  readonly createdAt: string | null;
}

export interface DashboardReconciliationAlert {
  readonly alertId: string | null;
  readonly type: string | null;
  readonly severity: string | null;
  readonly createdAt: string | null;
  readonly resolvedAt: string | null;
  readonly resolvedByUserId: string | null;
  readonly resolutionReason: string | null;
  readonly resolutionReference: string | null;
  readonly resolutionUpdatedAt: string | null;
}

export interface DashboardReconciliationPaymentDetail {
  readonly paymentId: string | null;
  readonly branchId: string | null;
  readonly saleId: string | null;
  readonly provider: string | null;
  readonly environment: string | null;
  readonly amountSatang: number;
  readonly currency: string | null;
  readonly reconciliationOutcome: string | null;
  readonly gatewayTruth: string | null;
  readonly projectionTruth: string | null;
  readonly createdAt: string | null;
  readonly updatedAt: string | null;
  readonly attempts: readonly DashboardReconciliationAttempt[];
  readonly events: readonly DashboardReconciliationEvent[];
  readonly settlements: readonly DashboardReconciliationSettlement[];
  readonly alerts: readonly DashboardReconciliationAlert[];
}

export interface DashboardReconciliationRunInput {
  readonly branchId: string;
  readonly from: string;
  readonly to: string;
  readonly idempotencyKey: string;
}

export interface DashboardReconciliationRunCreated {
  readonly runId: string;
  readonly status: 'created' | 'replay' | 'active';
}

export interface DashboardReconciliationRun {
  readonly runId: string | null;
  readonly branchId: string | null;
  readonly dateFrom: string | null;
  readonly dateTo: string | null;
  readonly triggerType: string | null;
  readonly status: 'queued' | 'running' | 'completed' | 'failed' | null;
  readonly processedCount: number;
  readonly matchedCount: number;
  readonly alertCount: number;
  readonly providerCallCount: number;
  readonly retryCount: number;
  readonly rateLimitedCount: number;
  readonly unverifiedCount: number;
  readonly createdAt: string | null;
  readonly startedAt: string | null;
  readonly completedAt: string | null;
  readonly errorCode: string | null;
}

export interface DashboardReconciliationAlertResolutionInput {
  readonly alertId: string;
  readonly reason: string;
  readonly reference?: string;
}

export interface DashboardReconciliationAlertResolution {
  readonly alertId: string;
  readonly status: 'resolved' | 'replay';
  readonly resolvedAt: string;
}

export interface DashboardReconciliationSettlementImportInput {
  readonly branchId: string;
  readonly file: Blob;
}

export interface DashboardReconciliationSettlementImportResult {
  readonly importId: string;
  readonly status: 'created' | 'replay';
  readonly rowCount: number;
  readonly parserVersion: 'internal-csv-v1';
  readonly retentionUntil: string;
}

export interface DashboardSettlementImportReconcileInput {
  readonly importId: string;
  readonly branchId: string;
  readonly from: string;
  readonly to: string;
  readonly idempotencyKey: string;
}

export interface DashboardSettlementImportReconcileResult {
  readonly batchId: string;
  readonly status: 'created' | 'replay';
  readonly importId: string;
  readonly branchId: string;
  readonly coverage: {
    readonly from: string;
    readonly to: string;
    readonly timeZone: 'Asia/Bangkok';
    readonly declaredComplete: true;
  };
  readonly counters: {
    readonly matched: number;
    readonly amountMismatch: number;
    readonly referenceMismatch: number;
    readonly duplicate: number;
    readonly orphan: number;
    readonly missingSettlement: number;
    readonly alerts: number;
  };
}

export interface DashboardReconciliationExportInput {
  readonly branchId: string;
  readonly from: string;
  readonly to: string;
  readonly status?: DashboardReconciliationStatus;
}

export type DashboardSettlementImportAnomalyType =
  | 'amount_mismatch'
  | 'reference_mismatch'
  | 'missing_settlement'
  | 'duplicate_payment'
  | 'orphan_payment'
  | 'unsettled_payment';

export interface DashboardSettlementImportAnomaly {
  readonly alertId: string;
  readonly settlementRowId: string | null;
  readonly alertType: DashboardSettlementImportAnomalyType;
  readonly severity: 'info' | 'warning' | 'critical';
  readonly status: 'open' | 'resolved';
  readonly createdAt: string;
  readonly resolvedAt: string | null;
}

export interface DashboardSettlementImportAnomalyListInput {
  readonly importId: string;
  readonly branchId: string;
  readonly limit?: number;
  readonly cursor?: string;
}

export interface DashboardSettlementImportAnomalyListResponse {
  readonly importId: string;
  readonly branchId: string;
  readonly items: readonly DashboardSettlementImportAnomaly[];
  readonly nextCursor: string | null;
}

export interface DashboardSettlementImportAnomalyDetailInput {
  readonly importId: string;
  readonly alertId: string;
  readonly branchId: string;
}

export interface DashboardSettlementImportAnomalyDetail {
  readonly importId: string;
  readonly branchId: string;
  readonly alert: DashboardSettlementImportAnomaly;
  readonly settlementRow: {
    readonly settlementRowId: string;
    readonly amountSatang: number;
    readonly currency: 'THB';
    readonly providerTransactionIdPresent: boolean;
    readonly referencePresence: {
      readonly primary: boolean;
      readonly reconciliation: boolean;
      readonly routing: boolean;
    };
  } | null;
  readonly match: {
    readonly outcome:
      | 'matched'
      | 'amount_mismatch'
      | 'reference_mismatch'
      | 'duplicate'
      | 'orphan_payment'
      | 'unsettled_payment';
    readonly expectedAmountSatang: number | null;
    readonly currency: 'THB' | null;
    readonly candidateEvidence: {
      readonly total: number;
      readonly truncated: boolean;
    };
  } | null;
}

export interface DashboardSettlementImportAnomalyResolutionInput extends DashboardSettlementImportAnomalyDetailInput {
  readonly reason: string;
  readonly resolutionReference: string;
}
export type {
  BackupListResponse,
  BackupObjectMetadata,
  DashboardEvidenceVerificationReport,
  DashboardInventoryReport,
  DashboardOverview,
  DashboardSalesReport,
  DashboardShiftReport,
  DashboardZReport,
} from '@pos/types';

type ContextResolver = () => DashboardApiContext;

let contextResolver: ContextResolver = resolveContextFromRuntime;

const STAFF_READABLE_MASTER_DATA = new Set<MasterDataEntity>([
  'branches',
  'categories',
  'products',
  'product_units',
  'product_barcodes',
  'branch_prices',
]);

const OWNER_ONLY_MASTER_DATA = new Set<MasterDataEntity>([
  'branches',
  'users',
  'settings',
]);

export function configureDashboardApiContext(
  next: DashboardApiContext | ContextResolver,
): void {
  contextResolver = typeof next === 'function' ? next : () => next;
}

export function resetDashboardApiContext(): void {
  contextResolver = resolveContextFromRuntime;
}

function baseUrl(): string {
  return import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:8787';
}

function requireEnv(name: string): string {
  const value = import.meta.env[name] as string | undefined;
  if (!value) {
    throw new DashboardApiError('DASH-003', 'ไม่พบค่าตั้งค่า API');
  }
  return value;
}

function resolveContextFromRuntime(): DashboardApiContext {
  const sessionUser = useSession.getState().user;
  const selectedBranchId = useUi.getState().branchId;
  const branchId =
    sessionUser?.role === 'owner'
      ? (selectedBranchId ??
        (sessionUser.branchIds.length === 1
          ? sessionUser.branchIds[0]
          : undefined) ??
        import.meta.env.VITE_API_BRANCH_ID)
      : (selectedBranchId ??
        sessionUser?.branchIds[0] ??
        import.meta.env.VITE_API_BRANCH_ID);
  const actorUserId = sessionUser?.id ?? import.meta.env.VITE_API_ACTOR_USER_ID;

  if (sessionUser && sessionUser.role !== 'owner') {
    if (!branchId || !sessionUser.branchIds.includes(branchId)) {
      appendPermissionDeniedAudit(
        `ปฏิเสธสาขา ${branchId ?? 'unknown'} สำหรับ role ${sessionUser.role}`,
      );
      throw new DashboardApiError('DASH-001', 'ไม่พบข้อมูลสาขา');
    }
  }

  if (!branchId) {
    throw new DashboardApiError('DASH-001', 'ไม่พบข้อมูลสาขา');
  }
  if (!actorUserId) {
    throw new DashboardApiError('DASH-002', 'ไม่พบข้อมูลผู้ใช้');
  }

  return {
    tenantId: requireEnv('VITE_API_TENANT_ID'),
    branchId,
    actorUserId,
    affectedDeviceIds:
      (import.meta.env.VITE_API_AFFECTED_DEVICE_IDS as string | undefined)
        ?.split(',')
        .map((value) => value.trim())
        .filter(Boolean) ?? [],
  };
}

function getContext(
  override?: Partial<DashboardApiContext>,
): DashboardApiContext {
  return { ...contextResolver(), ...override };
}

function canReadMasterDataEntity(
  user: BoUser,
  entity: MasterDataEntity,
): boolean {
  if (user.role === 'owner' || user.role === 'manager') {
    return true;
  }

  return STAFF_READABLE_MASTER_DATA.has(entity);
}

function canWriteMasterDataEntity(
  user: BoUser,
  entity: MasterDataEntity,
): boolean {
  if (entity === 'promotions') {
    return canEditPromotions(user);
  }

  if (OWNER_ONLY_MASTER_DATA.has(entity)) {
    return canEditSystemConfig(user);
  }

  return canEditMasterData(user);
}

function assertMasterDataAccess(
  entity: MasterDataEntity,
  mode: 'read' | 'write',
): void {
  const user = useSession.getState().user;
  if (!user) {
    return;
  }

  const allowed =
    mode === 'read'
      ? canReadMasterDataEntity(user, entity)
      : canWriteMasterDataEntity(user, entity);

  if (allowed) {
    return;
  }

  appendPermissionDeniedAudit(
    `ปฏิเสธ ${mode === 'read' ? 'อ่าน' : 'แก้ไข'} ${entity} สำหรับ role ${user.role}`,
  );
  throw new DashboardApiError('DASH-004', 'ไม่มีสิทธิ์ใช้งานข้อมูลนี้');
}

function requestInit(init: RequestInit = {}): RequestInit {
  const headers = new Headers(init.headers);
  const token = useSession.getState().token;
  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }
  return { ...init, headers };
}

function withQuery(
  path: string,
  params: Record<string, string | string[] | undefined>,
): string {
  const searchParams = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (Array.isArray(value)) {
      value.filter(Boolean).forEach((item) => searchParams.append(key, item));
    } else if (value) {
      searchParams.set(key, value);
    }
  }
  const query = searchParams.toString();
  return query ? `${path}?${query}` : path;
}

function backupDateBoundary(
  value: string | undefined,
  boundary: 'start' | 'end',
) {
  if (!value) return undefined;
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  const time = boundary === 'start' ? '00:00:00.000' : '23:59:59.999';
  return new Date(`${value}T${time}`).toISOString();
}

async function request<T>(
  path: string,
  init?: RequestInit,
  hasRetriedAuth = false,
): Promise<T> {
  const response = await fetch(`${baseUrl()}${path}`, requestInit(init));
  if (
    response.status === 401 &&
    !hasRetriedAuth &&
    (await refreshStoredSession())
  ) {
    return request<T>(path, init, true);
  }
  const body = (await response.json()) as
    ApiSuccessEnvelope<T> | ApiErrorEnvelope | { error?: { message?: string } };
  if (!response.ok || !('ok' in body) || !body.ok) {
    if ('ok' in body && body.ok === false) {
      throw new DashboardApiError({
        code: body.error.code,
        message: body.error.message,
        status: response.status,
        details: body.error.details,
        requestId:
          response.headers?.get?.('x-request-id') ??
          (typeof body.error.details?.requestId === 'string'
            ? body.error.details.requestId
            : undefined),
        endpoint: path.split('?')[0],
      });
    }
    const message =
      'error' in body && body.error?.message
        ? body.error.message
        : 'เรียกใช้งาน API ไม่สำเร็จ';
    throw new DashboardApiError('DASH-005', message);
  }

  return body.data;
}

async function requestBlob(path: string, init?: RequestInit): Promise<Blob> {
  const response = await fetch(`${baseUrl()}${path}`, requestInit(init));
  if (!response.ok) {
    const contentType = response.headers.get('content-type') ?? '';
    if (contentType.includes('application/json')) {
      const body = (await response.json()) as {
        error?: {
          code?: string;
          message?: string;
          details?: Record<string, unknown>;
        };
      };
      if (body.error?.message) {
        throw new DashboardApiError({
          code: body.error.code ?? 'DASH-005',
          message: body.error.message,
          status: response.status,
          details: body.error.details,
          requestId: response.headers?.get?.('x-request-id') ?? undefined,
          endpoint: path.split('?')[0],
        });
      }
    }
    throw new DashboardApiError('DASH-005', 'เรียกใช้งาน API ไม่สำเร็จ');
  }
  return response.blob();
}

async function login(input: AuthLoginRequest): Promise<AuthLoginResponse> {
  return request<AuthLoginResponse>('/auth/login', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(input),
  });
}

async function changePassword(input: {
  currentPassword: string;
  newPassword: string;
}): Promise<{ changed: true }> {
  return request<{ changed: true }>('/auth/change-password', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(input),
  });
}

async function refreshStoredSession(): Promise<boolean> {
  const { refreshToken, user, setAuthTokens } = useSession.getState();
  if (!refreshToken || !user) {
    return false;
  }
  const response = await fetch(`${baseUrl()}/auth/refresh`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ refreshToken }),
  });
  if (!response.ok) {
    return false;
  }
  const body =
    (await response.json()) as ApiSuccessEnvelope<TokenRefreshResponse>;
  if (!body.ok) {
    return false;
  }
  setAuthTokens(body.data.token, body.data.refreshToken, body.data.expiresAt);
  return true;
}

async function logout(): Promise<void> {
  const refreshToken = useSession.getState().refreshToken;
  if (!refreshToken) {
    return;
  }
  await request<{ revoked: boolean }>('/auth/logout', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ refreshToken }),
  });
}

async function readMasterCollection<T>(
  entity: MasterDataEntity,
  context?: Partial<DashboardApiContext>,
): Promise<T[]> {
  assertMasterDataAccess(entity, 'read');
  const resolvedContext = getContext(context);
  const searchParams = new URLSearchParams({
    entity,
    tenantId: resolvedContext.tenantId,
    branchId: resolvedContext.branchId,
  });
  const result = await request<MasterDataPullResponse<T>>(
    `/master-data?${searchParams.toString()}`,
  );
  return result.changes
    .filter((change) => change.op === 'upsert' && change.data)
    .map((change) => change.data as T);
}

async function writeMasterEntity<T>(
  entity: MasterDataEntity,
  data: T & { id?: string },
  context?: Partial<DashboardApiContext>,
): Promise<MasterDataVersionEnvelope> {
  assertMasterDataAccess(entity, 'write');
  const resolvedContext = getContext(context);
  return request<MasterDataVersionEnvelope>(`/master-data/${entity}`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({
      tenantId: resolvedContext.tenantId,
      branchIds: [resolvedContext.branchId],
      actorUserId: resolvedContext.actorUserId,
      deviceIds: resolvedContext.affectedDeviceIds,
      data,
    } satisfies MasterDataWriteRequest<T>),
  });
}

async function deleteMasterEntity(
  entity: MasterDataEntity,
  id: string,
  context?: Partial<DashboardApiContext>,
): Promise<MasterDataVersionEnvelope> {
  assertMasterDataAccess(entity, 'write');
  getContext(context);
  return request<MasterDataVersionEnvelope>(`/master-data/${entity}/${id}`, {
    method: 'DELETE',
  });
}

const DASHBOARD_VAT_PERCENT_SCALE = 100;

function mapApiSettingsToDashboard(
  settings: (SystemSettings & { id?: string }) | undefined,
): SystemSettings {
  return {
    shopName: settings?.shopName ?? '',
    taxId: settings?.taxId ?? '',
    vatRate:
      typeof settings?.vatRate === 'number'
        ? settings.vatRate / DASHBOARD_VAT_PERCENT_SCALE
        : 0,
    vatInclusive: settings?.vatInclusive ?? true,
    receiptHeader: settings?.receiptHeader ?? '',
    receiptFooter: settings?.receiptFooter ?? '',
  };
}

function mapDashboardSettingsToApi(
  settings: SystemSettings,
  current: (SharedSystemSettings & { id?: string }) | undefined,
): SharedSystemSettings & { id: string } {
  return {
    ...DEFAULT_SYSTEM_SETTINGS,
    ...current,
    id: current?.id ?? 'settings',
    shopName: settings.shopName,
    taxId: settings.taxId,
    vatRate: Math.round(settings.vatRate * DASHBOARD_VAT_PERCENT_SCALE),
    vatInclusive: settings.vatInclusive,
    receiptHeader: settings.receiptHeader,
    receiptFooter: settings.receiptFooter,
  };
}

interface ApiProductRecord {
  id: string;
  name: string;
  categoryId: string;
  baseUnitId?: string;
  baseUnit?: string;
  active: boolean;
  minStock: number;
  cost?: number;
  imageUrl?: string;
  units?: ProductUnit[];
}

interface ApiProductUnitRecord {
  id: string;
  productId: string;
  code: string;
  name: string;
  ratio: number;
  salePrice: number;
  active: boolean;
}

interface ApiProductBarcodeRecord {
  id: string;
  productUnitId: string;
  barcode: string;
  active: boolean;
}

interface ApiBranchPriceRecord {
  id: string;
  branchId: string;
  productId: string;
  unitId: string;
  price: number;
}

interface ApiPaymentMethodRecord {
  id: PaymentMethodConfig['id'];
  name: string;
  active?: boolean;
}

async function readCurrentSettings(
  context?: Partial<DashboardApiContext>,
): Promise<(SharedSystemSettings & { id?: string }) | undefined> {
  const rows = await readMasterCollection<
    SharedSystemSettings & { id?: string }
  >('settings', context);
  return rows[0];
}

function mapApiProductToDashboard(
  product: ApiProductRecord,
  units: ApiProductUnitRecord[],
  barcodes: ApiProductBarcodeRecord[],
): Product {
  if (units.length === 0 && product.units) {
    return {
      id: product.id,
      name: product.name,
      categoryId: product.categoryId,
      baseUnit: product.baseUnit ?? product.units[0]?.unit ?? '',
      baseUnitId: product.baseUnitId,
      units: product.units,
      active: product.active,
      minStock: product.minStock,
      cost: product.cost,
      imageUrl: product.imageUrl,
    };
  }

  const barcodeByUnitId = new Map(
    barcodes
      .filter((record) => record.active)
      .map((record) => [record.productUnitId, record]),
  );
  const mappedUnits: ProductUnit[] = units
    .filter((unit) => unit.active)
    .sort((left, right) => left.ratio - right.ratio)
    .map((unit) => ({
      id: unit.id,
      code: unit.code,
      unit: unit.name,
      factor: unit.ratio,
      price: unit.salePrice,
      barcode: barcodeByUnitId.get(unit.id)?.barcode ?? '',
      barcodeId: barcodeByUnitId.get(unit.id)?.id,
    }));
  const baseUnit =
    mappedUnits.find((unit) => unit.id === product.baseUnitId)?.unit ??
    mappedUnits[0]?.unit ??
    '';

  return {
    id: product.id,
    name: product.name,
    categoryId: product.categoryId,
    baseUnit,
    baseUnitId: product.baseUnitId,
    units: mappedUnits,
    active: product.active,
    minStock: product.minStock,
    cost: product.cost,
    imageUrl: product.imageUrl,
  };
}

function deriveUnitCode(label: string, index: number): string {
  const normalized = label
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
  return normalized || `UNIT_${index + 1}`;
}

export function resolveProductUnitId(
  productId: string,
  existingUnitId: string | undefined,
  index: number,
  claimedUnitIds: ReadonlySet<string>,
): string {
  if (existingUnitId) return existingUnitId;
  const positionalUnitId = `${productId}:unit:${index + 1}`;
  return claimedUnitIds.has(positionalUnitId)
    ? `${productId}:unit:${globalThis.crypto.randomUUID()}`
    : positionalUnitId;
}

// ── Promotion type adapters ──────────────────────────────────
// Dashboard uses percent | baht | bogo | qty_tier; API/shared-contract uses
// percent_off | fixed_price | bogo | qty_tier | bill_percent.
// These two helpers map in both directions.

/**
 * Map API/shared-contract Promotion → dashboard Promotion type.
 * Unknown types (such as bill_percent) throw an error to prevent
 * silent data corruption on round-trip edit+save.
 */
function mapApiPromotionToDashboard(p: SharedPromotion): Promotion {
  const dashType: Promotion['type'] =
    p.type === 'fixed_price'
      ? 'baht'
      : p.type === 'percent_off'
        ? 'percent'
        : p.type === 'bogo'
          ? 'bogo'
          : p.type === 'qty_tier'
            ? 'qty_tier'
            : (() => {
                throw new DashboardApiError(
                  'DASH-006',
                  'ไม่สามารถโหลดโปรโมชั่นประเภทนี้ได้',
                );
              })();

  // Derive status from shared fields
  const now = new Date();
  const start = new Date(p.startDate);
  const end = new Date(p.endDate);
  const status: Promotion['status'] =
    p.active === false
      ? 'ended'
      : now < start
        ? 'scheduled'
        : now > end
          ? 'ended'
          : 'active';

  return {
    id: p.id,
    name: p.name,
    type: dashType,
    value: p.value,
    status,
    startDate: p.startDate,
    endDate: p.endDate,
    scope: p.branchIds.length > 0 ? 'specific' : 'all',
    branchIds: p.branchIds,
    minBaht: p.minBaht,
    minQty: p.minQty,
    perBillLimit: p.perBillLimit,
    productIds: p.productIds,
  };
}

/** Map dashboard Promotion → API/shared-contract Promotion. */
function mapDashboardPromotionToApi(p: Promotion): SharedPromotion {
  const sharedType: SharedPromotion['type'] =
    p.type === 'baht'
      ? 'fixed_price'
      : p.type === 'percent'
        ? 'percent_off'
        : p.type === 'qty_tier'
          ? 'qty_tier'
          : 'bogo';

  // active = true if status is active or scheduled
  const active = p.status !== 'ended';

  return {
    id: p.id,
    name: p.name,
    type: sharedType,
    value: p.value,
    startDate: p.startDate,
    endDate: p.endDate,
    branchIds: p.branchIds,
    active,
    minBaht: p.minBaht,
    minQty: p.minQty,
    perBillLimit: p.perBillLimit,
    productIds: p.productIds,
  };
}

export const dashboardApi = {
  listPaymentReviews: (
    branchId: string,
    includeCompleted = false,
    offset = 0,
  ) =>
    request<import('@pos/types').PaymentReviewPage>(
      withQuery('/dashboard/payments/operations', {
        branchId,
        includeCompleted: String(includeCompleted),
        offset: String(offset),
      }),
    ),
  releasePaymentReview: (paymentId: string, reason: string) =>
    request<import('@pos/types').PaymentReviewDetail>(
      `/dashboard/payments/operations/${encodeURIComponent(paymentId)}/release`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason }),
      },
    ),
  getPaymentReview: (paymentId: string) =>
    request<import('@pos/types').PaymentReviewDetail>(
      `/dashboard/payments/operations/${encodeURIComponent(paymentId)}`,
    ),
  attachPaymentSlip: (
    paymentId: string,
    input: { qrPayload: string; imageBase64: string; contentType: string },
  ) =>
    request<import('@pos/types').PaymentReviewDetail>(
      `/dashboard/payments/operations/${encodeURIComponent(paymentId)}/slips`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(input),
      },
    ),
  verifyPaymentSlip: (paymentId: string, evidenceId: string) =>
    request<import('@pos/types').PaymentReviewDetail>(
      `/dashboard/payments/operations/${encodeURIComponent(paymentId)}/slips/${encodeURIComponent(evidenceId)}/verify`,
      { method: 'POST' },
    ),
  getPaymentSlipImage: (paymentId: string, evidenceId: string) =>
    requestBlob(
      `/dashboard/payments/operations/${encodeURIComponent(paymentId)}/slips/${encodeURIComponent(evidenceId)}`,
    ),
  voidScbPayment: (paymentId: string, reason: string) =>
    request<import('@pos/types').PaymentReviewDetail>(
      `/dashboard/payments/operations/${encodeURIComponent(paymentId)}/void`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason }),
      },
    ),
  checkScbVoid: (paymentId: string) =>
    request<import('@pos/types').PaymentReviewDetail>(
      `/dashboard/payments/operations/${encodeURIComponent(paymentId)}/void/check`,
      { method: 'POST' },
    ),
  createPriceChecker: (input: { branchId: string; label: string }) =>
    request<PriceCheckerRegistration>('/dashboard/price-checkers', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    }),
  getPriceChecker: (deviceId: string) =>
    request<PriceCheckerDevice>(
      `/dashboard/price-checkers/${encodeURIComponent(deviceId)}`,
    ),
  revokePriceChecker: (deviceId: string) =>
    request<{ revoked: boolean }>(
      `/dashboard/price-checkers/${encodeURIComponent(deviceId)}`,
      { method: 'DELETE' },
    ),
  login,
  changePassword,
  logout,
  listBranches: (context?: Partial<DashboardApiContext>): Promise<Branch[]> =>
    readMasterCollection<Branch>('branches', context),
  listCategories: (
    context?: Partial<DashboardApiContext>,
  ): Promise<Category[]> =>
    readMasterCollection<Category>('categories', context),
  listProducts: async (
    context?: Partial<DashboardApiContext>,
  ): Promise<Product[]> => {
    const [products, units, barcodes] = await Promise.all([
      readMasterCollection<ApiProductRecord>('products', context),
      readMasterCollection<ApiProductUnitRecord>('product_units', context),
      readMasterCollection<ApiProductBarcodeRecord>(
        'product_barcodes',
        context,
      ),
    ]);

    const productIdByUnitId = new Map(
      units.map((unit) => [unit.id, unit.productId]),
    );
    const unitsByProductId = new Map<string, ApiProductUnitRecord[]>();
    for (const unit of units) {
      const productUnits = unitsByProductId.get(unit.productId) ?? [];
      productUnits.push(unit);
      unitsByProductId.set(unit.productId, productUnits);
    }
    const barcodesByProductId = new Map<string, ApiProductBarcodeRecord[]>();
    for (const barcode of barcodes) {
      const productId = productIdByUnitId.get(barcode.productUnitId);
      if (!productId) continue;
      const productBarcodes = barcodesByProductId.get(productId) ?? [];
      productBarcodes.push(barcode);
      barcodesByProductId.set(productId, productBarcodes);
    }

    return products.map((product) =>
      mapApiProductToDashboard(
        product,
        unitsByProductId.get(product.id) ?? [],
        barcodesByProductId.get(product.id) ?? [],
      ),
    );
  },
  listProductsPage: async (
    input?: Omit<DashboardProductListQuery, 'tenantId'>,
  ): Promise<DashboardProductListResponse> =>
    request<DashboardProductListResponse>(
      withQuery('/dashboard/products', {
        limit: input?.limit ? String(input.limit) : undefined,
        offset: input?.offset ? String(input.offset) : undefined,
        search: input?.search,
        categoryId: input?.categoryId,
        status: input?.status,
        id: input?.ids,
      }),
    ),
  listUsers: (context?: Partial<DashboardApiContext>): Promise<User[]> =>
    readMasterCollection<User>('users', context),
  listBranchPricing: async (
    context?: Partial<DashboardApiContext>,
  ): Promise<BranchPricing[]> => {
    const [products, branchPrices] = await Promise.all([
      readMasterCollection<ApiProductRecord>('products', context),
      readMasterCollection<ApiBranchPriceRecord>('branch_prices', context),
    ]);
    const baseUnitByProductId = new Map(
      products.map((product) => [product.id, product.baseUnitId]),
    );
    const rows = new Map<string, BranchPricing>();

    for (const record of branchPrices) {
      const baseUnitId = baseUnitByProductId.get(record.productId);
      if (!baseUnitId || record.unitId !== baseUnitId) {
        continue;
      }
      const existing = rows.get(record.productId) ?? {
        productId: record.productId,
        prices: {},
      };
      existing.prices[record.branchId] = record.price;
      rows.set(record.productId, existing);
    }

    return Array.from(rows.values());
  },
  listPaymentMethods: async (
    context?: Partial<DashboardApiContext>,
  ): Promise<PaymentMethodConfig[]> => {
    const rows = await readMasterCollection<ApiPaymentMethodRecord>(
      'payment_methods',
      context,
    );
    return rows.map((row) => ({
      id: row.id,
      name: row.name,
      active: row.active ?? true,
    }));
  },
  listBranchPaymentSettings: (
    context?: Partial<DashboardApiContext>,
  ): Promise<BranchPaymentSettings[]> =>
    readMasterCollection<BranchPaymentSettings>(
      'branch_payment_settings',
      context,
    ),
  listManualPaymentChannels: (
    context?: Partial<DashboardApiContext>,
  ): Promise<ManualPaymentChannel[]> =>
    readMasterCollection<ManualPaymentChannel>(
      'manual_payment_channels',
      context,
    ),
  listBranchManualPaymentChannels: (
    context?: Partial<DashboardApiContext>,
  ): Promise<BranchManualPaymentChannel[]> =>
    readMasterCollection<BranchManualPaymentChannel>(
      'branch_manual_payment_channels',
      context,
    ),
  listPromotions: async (
    context?: Partial<DashboardApiContext>,
  ): Promise<Promotion[]> => {
    // Read API as shared-contract type, then map to dashboard type.
    const rows = await readMasterCollection<SharedPromotion>(
      'promotions',
      context,
    );
    return rows.map(mapApiPromotionToDashboard);
  },
  listSettings: async (
    context?: Partial<DashboardApiContext>,
  ): Promise<SystemSettings> => {
    const rows = await readMasterCollection<SystemSettings & { id: string }>(
      'settings',
      context,
    );
    return mapApiSettingsToDashboard(rows[0]);
  },
  getOverview: (input: {
    date: string;
    branchId?: string;
  }): Promise<DashboardOverview> =>
    request<DashboardOverview>(
      withQuery('/dashboard/overview', {
        date: input.date,
        branchId: input.branchId,
      }),
    ),
  listReconciliation: (
    input: DashboardReconciliationListInput,
  ): Promise<DashboardReconciliationListResponse> =>
    request<DashboardReconciliationListResponse>(
      withQuery('/dashboard/payments/reconciliation', {
        from: input.from,
        to: input.to,
        branchId: input.branchId,
        status: input.status,
        limit: input.limit === undefined ? undefined : String(input.limit),
        cursor: input.cursor,
      }),
    ),
  getReconciliationPayment: (
    paymentId: string,
  ): Promise<DashboardReconciliationPaymentDetail> =>
    request<DashboardReconciliationPaymentDetail>(
      `/dashboard/payments/reconciliation/payments/${encodeURIComponent(paymentId)}`,
    ),
  probeReconciliationPaymentGateway: (
    input: DashboardGatewayProbeInput,
  ): Promise<DashboardGatewayProbe> =>
    request<DashboardGatewayProbe>(
      `/dashboard/payments/reconciliation/payments/${encodeURIComponent(input.paymentId)}/probe`,
      {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ branchId: input.branchId }),
      },
    ),
  createReconciliationRun: (
    input: DashboardReconciliationRunInput,
  ): Promise<DashboardReconciliationRunCreated> =>
    request<DashboardReconciliationRunCreated>(
      '/dashboard/payments/reconciliation/run',
      {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
          'idempotency-key': input.idempotencyKey,
        },
        body: JSON.stringify({
          branchId: input.branchId,
          from: input.from,
          to: input.to,
        }),
      },
    ),
  getReconciliationRun: (runId: string): Promise<DashboardReconciliationRun> =>
    request<DashboardReconciliationRun>(
      `/dashboard/payments/reconciliation/runs/${encodeURIComponent(runId)}`,
    ),
  importReconciliationSettlementCsv: (
    input: DashboardReconciliationSettlementImportInput,
  ): Promise<DashboardReconciliationSettlementImportResult> =>
    request<DashboardReconciliationSettlementImportResult>(
      withQuery('/dashboard/payments/reconciliation/imports', {
        branchId: input.branchId,
      }),
      {
        method: 'POST',
        headers: { 'content-type': 'text/csv; charset=utf-8' },
        body: input.file,
      },
    ),
  reconcileSettlementImport: (
    input: DashboardSettlementImportReconcileInput,
  ): Promise<DashboardSettlementImportReconcileResult> =>
    request<DashboardSettlementImportReconcileResult>(
      `/dashboard/payments/reconciliation/imports/${encodeURIComponent(input.importId)}/reconcile`,
      {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
          'idempotency-key': input.idempotencyKey,
        },
        body: JSON.stringify({
          branchId: input.branchId,
          from: input.from,
          to: input.to,
        }),
      },
    ),
  listSettlementImportAnomalies: (
    input: DashboardSettlementImportAnomalyListInput,
  ): Promise<DashboardSettlementImportAnomalyListResponse> =>
    request<DashboardSettlementImportAnomalyListResponse>(
      withQuery(
        `/dashboard/payments/reconciliation/imports/${encodeURIComponent(input.importId)}/anomalies`,
        {
          branchId: input.branchId,
          limit: input.limit === undefined ? undefined : String(input.limit),
          cursor: input.cursor,
        },
      ),
    ),
  getSettlementImportAnomaly: (
    input: DashboardSettlementImportAnomalyDetailInput,
  ): Promise<DashboardSettlementImportAnomalyDetail> =>
    request<DashboardSettlementImportAnomalyDetail>(
      withQuery(
        `/dashboard/payments/reconciliation/imports/${encodeURIComponent(input.importId)}/anomalies/${encodeURIComponent(input.alertId)}`,
        { branchId: input.branchId },
      ),
    ),
  resolveSettlementImportAnomaly: (
    input: DashboardSettlementImportAnomalyResolutionInput,
  ): Promise<DashboardReconciliationAlertResolution> =>
    request<DashboardReconciliationAlertResolution>(
      `/dashboard/payments/reconciliation/imports/${encodeURIComponent(input.importId)}/anomalies/${encodeURIComponent(input.alertId)}/resolve`,
      {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          branchId: input.branchId,
          reason: input.reason,
          resolutionReference: input.resolutionReference,
        }),
      },
    ),
  exportReconciliationCsv: (
    input: DashboardReconciliationExportInput,
  ): Promise<Blob> =>
    requestBlob(
      withQuery('/dashboard/payments/reconciliation/export', {
        branchId: input.branchId,
        from: input.from,
        to: input.to,
        status: input.status,
      }),
    ),
  resolveReconciliationAlert: (
    input: DashboardReconciliationAlertResolutionInput,
  ): Promise<DashboardReconciliationAlertResolution> =>
    request<DashboardReconciliationAlertResolution>(
      `/dashboard/payments/reconciliation/alerts/${encodeURIComponent(input.alertId)}/resolve`,
      {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          reason: input.reason,
          ...(input.reference === undefined
            ? {}
            : { reference: input.reference }),
        }),
      },
    ),
  listTransfers: (input: {
    branchId: string;
  }): Promise<{ transfers: DashboardTransfer[] }> =>
    request<{ transfers: DashboardTransfer[] }>(
      withQuery('/dashboard/transfers', { branchId: input.branchId }),
    ),
  getTransfer: (id: string): Promise<DashboardTransfer> =>
    request<DashboardTransfer>(
      `/dashboard/transfers/${encodeURIComponent(id)}`,
    ),
  createTransfer: (
    input: DashboardTransferCreateInput,
  ): Promise<DashboardTransfer> =>
    request<DashboardTransfer>('/dashboard/transfers', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(input),
    }),
  receiveTransfer: (
    id: string,
    input: DashboardTransferReceiveInput,
  ): Promise<DashboardTransfer> =>
    request<DashboardTransfer>(
      `/dashboard/transfers/${encodeURIComponent(id)}/receive`,
      {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(input),
      },
    ),
  createWholesaleSale: (
    input: DashboardWholesaleSaleCreateInput,
  ): Promise<DashboardWholesaleSale> =>
    request<DashboardWholesaleSale>('/dashboard/wholesale-sales', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(input),
    }),
  getSalesReport: async (input: {
    from: string;
    to: string;
    branchId?: string;
  }): Promise<DashboardSalesReport> =>
    request<DashboardSalesReport>(
      withQuery('/dashboard/reports/sales', {
        from: input.from,
        to: input.to,
        branchId: input.branchId,
      }),
    ),
  getProductSalesDetail: async (input: {
    productId: string;
    from: string;
    to: string;
    branchId?: string;
  }): Promise<DashboardProductSalesDetailReport> =>
    request<DashboardProductSalesDetailReport>(
      withQuery('/dashboard/reports/product-detail', {
        productId: input.productId,
        from: input.from,
        to: input.to,
        branchId: input.branchId,
      }),
    ),
  getInventoryReport: async (input?: {
    branchId?: string;
  }): Promise<DashboardInventoryReport> =>
    request<DashboardInventoryReport>(
      withQuery('/dashboard/reports/inventory', {
        branchId: input?.branchId,
      }),
    ),
  getShiftReport: async (input?: {
    from?: string;
    to?: string;
    branchId?: string;
  }): Promise<DashboardShiftReport> =>
    request<DashboardShiftReport>(
      withQuery('/dashboard/reports/shifts', {
        from: input?.from,
        to: input?.to,
        branchId: input?.branchId,
      }),
    ),
  getEvidenceVerificationReport: async (input: {
    from: string;
    to: string;
    branchId?: string;
  }): Promise<DashboardEvidenceVerificationReport> =>
    request<DashboardEvidenceVerificationReport>(
      withQuery('/dashboard/reports/evidence-verification', {
        from: input.from,
        to: input.to,
        branchId: input.branchId,
      }),
    ),
  getZReport: async (input: {
    date: string;
    branchId?: string;
  }): Promise<DashboardZReport> =>
    request<DashboardZReport>(
      withQuery('/dashboard/reports/z-report', {
        date: input.date,
        branchId: input.branchId,
      }),
    ),
  listReceipts: async (input: {
    from: string;
    to: string;
    branchId?: string;
    receiptNo?: string;
    taxInvoiceIssuedOnly?: boolean;
    limit?: number;
    offset?: number;
  }): Promise<DashboardReceiptListResponse> =>
    request<DashboardReceiptListResponse>(
      withQuery('/dashboard/receipts', {
        from: input.from,
        to: input.to,
        branchId: input.branchId,
        receiptNo: input.receiptNo,
        taxInvoiceIssuedOnly: input.taxInvoiceIssuedOnly ? 'true' : undefined,
        limit: input.limit === undefined ? undefined : String(input.limit),
        offset: input.offset === undefined ? undefined : String(input.offset),
      }),
    ),
  getReceipt: async (input: {
    receiptNo: string;
    branchId?: string;
    from?: string;
    to?: string;
  }): Promise<DashboardReceiptDetailResponse> =>
    request<DashboardReceiptDetailResponse>(
      withQuery(`/dashboard/receipts/${encodeURIComponent(input.receiptNo)}`, {
        branchId: input.branchId,
        from: input.from,
        to: input.to,
      }),
    ),
  issueTaxInvoice: async (
    receiptNo: string,
    branchId?: string,
    customer?: TaxInvoiceCustomerInput,
  ): Promise<DashboardTaxInvoiceIssueResponse> =>
    request<DashboardTaxInvoiceIssueResponse>(
      withQuery(
        `/dashboard/receipts/${encodeURIComponent(receiptNo)}/tax-invoice`,
        { branchId },
      ),
      {
        method: 'POST',
        ...(customer
          ? {
              headers: { 'content-type': 'application/json' },
              body: JSON.stringify({ customer }),
            }
          : {}),
      },
    ),
  searchTaxInvoiceCustomers: async (input: {
    query: string;
    branchId?: string;
    limit?: number;
  }): Promise<DashboardTaxInvoiceCustomerSearchResponse> =>
    request<DashboardTaxInvoiceCustomerSearchResponse>(
      withQuery('/dashboard/tax-invoice-customers', {
        branchId: input.branchId,
      }),
      {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          query: input.query,
          ...(input.limit === undefined ? {} : { limit: input.limit }),
        }),
      },
    ),
  downloadZReport: async (input: {
    date: string;
    branchId?: string;
    format: 'pdf' | 'excel';
  }): Promise<Blob> =>
    requestBlob(
      withQuery('/dashboard/reports/z-report', {
        date: input.date,
        branchId: input.branchId,
        format: input.format,
      }),
    ),
  listBackups: async (input: {
    branchId: string;
    deviceId?: string;
    from?: string;
    to?: string;
  }): Promise<BackupListResponse> =>
    request<BackupListResponse>(
      withQuery('/backups', {
        branchId: input.branchId,
        deviceId: input.deviceId,
        from: backupDateBoundary(input.from, 'start'),
        to: backupDateBoundary(input.to, 'end'),
      }),
    ),
  downloadBackup: async (objectKey: string): Promise<Blob> =>
    requestBlob(`/${objectKey.replace(/^\/+/, '')}`),
  getDeviceHealth: async (input?: {
    branchId?: string;
  }): Promise<DeviceHealthQueryResponse> =>
    request<DeviceHealthQueryResponse>(
      withQuery('/device-health', {
        branchId: input?.branchId,
      }),
    ),
  listAudit: async (input?: {
    limit?: number;
    offset?: number;
    actorUserId?: string;
    category?: AuditCategory;
    branchId?: string;
  }): Promise<{ entries: AuditLogEntry[]; total: number }> =>
    request<{ entries: AuditLogEntry[]; total: number }>(
      withQuery('/dashboard/audit', {
        limit: input?.limit ? String(input.limit) : undefined,
        offset: input?.offset ? String(input.offset) : undefined,
        actorUserId: input?.actorUserId,
        category: input?.category,
        branchId: input?.branchId,
      }),
    ),
  createDeviceEnrollment: async (input: {
    branchId: string;
    label: string;
  }): Promise<CreateEnrollmentResponse> => {
    return request<CreateEnrollmentResponse>('/devices/enroll', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({
        branchId: input.branchId,
        label: input.label,
      }),
    });
  },
  revokeDevice: (deviceId: string): Promise<DeviceManagementRecord> =>
    request(`/devices/${encodeURIComponent(deviceId)}/revoke`, {
      method: 'POST',
    }),
  createDeviceRecoveryCode: (
    deviceId: string,
    input: CreateDeviceRecoveryCodeRequest,
  ): Promise<CreateDeviceRecoveryCodeResponse> =>
    request(`/devices/${encodeURIComponent(deviceId)}/recovery-code`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(input),
    }),
  getSyncHealthReport: async (input: {
    deviceId: string;
    branchId?: string;
  }): Promise<SyncHealthReport> =>
    request<SyncHealthReport>(
      withQuery('/sync-health', {
        deviceId: input.deviceId,
        branchId: input.branchId,
      }),
    ),
  addStockMovement: async (input: {
    branchId: string;
    type: 'adjust' | 'receive';
    requestId?: string;
    productId: string;
    unitId: string;
    quantity: number;
    unitCost?: number;
    reason: string;
    occurredAt?: string;
  }): Promise<{ movementId: string }> => {
    const context = getContext({ branchId: input.branchId });
    return request<{ movementId: string }>('/dashboard/stock-movements', {
      method: 'POST',
      body: JSON.stringify({
        tenantId: context.tenantId,
        branchId: input.branchId,
        type: input.type,
        requestId: input.requestId ?? crypto.randomUUID(),
        productId: input.productId,
        unitId: input.unitId,
        quantity: input.quantity,
        ...(input.type === 'receive' ? { unitCost: input.unitCost } : {}),
        reason: input.reason,
        occurredAt: input.occurredAt ?? new Date().toISOString(),
      }),
      headers: { 'content-type': 'application/json' },
    });
  },
  listStockByBranch: async (input: {
    branchId: string;
  }): Promise<StockRow[]> => {
    getContext({ branchId: input.branchId });
    // /dashboard/stock is a cursor-paginated movement feed (500/page, ascending
    // by occurredAt) carrying a running onHandQuantity per product. We must walk
    // every page — reading only the first would compute on-hand from the OLDEST
    // 500 movements and miss/stale any product that moved past the cutoff.
    const latestByProduct = new Map<string, StockRow>();
    let cursor: string | undefined;
    do {
      const page = await request<{
        movements: Array<{
          movementId: string;
          branchId: string;
          productId: string;
          onHandQuantity: number;
        }>;
        hasMore: boolean;
        nextCursor?: string;
      }>(
        withQuery('/dashboard/stock', {
          branchId: input.branchId,
          since: cursor,
        }),
      );
      for (const movement of page.movements) {
        latestByProduct.set(movement.productId, {
          branchId: movement.branchId,
          productId: movement.productId,
          onHand: movement.onHandQuantity,
        });
      }
      cursor = page.hasMore ? page.nextCursor : undefined;
    } while (cursor);
    return Array.from(latestByProduct.values());
  },
  listStockMovements: async (input: {
    branchId: string;
  }): Promise<StockMovement[]> => {
    getContext({ branchId: input.branchId });
    const movements: StockMovement[] = [];
    let cursor: string | undefined;
    do {
      const page = await request<{
        movements: Array<{
          movementId: string;
          branchId: string;
          productId: string;
          movementType: string;
          quantity: number;
          occurredAt: string;
        }>;
        hasMore: boolean;
        nextCursor?: string;
      }>(
        withQuery('/dashboard/stock', {
          branchId: input.branchId,
          since: cursor,
        }),
      );
      for (const movement of page.movements) {
        const type: StockMovement['type'] =
          movement.movementType === 'sale'
            ? 'sale'
            : movement.movementType === 'receive' ||
                movement.movementType === 'stock_in'
              ? 'receive'
              : movement.movementType === 'transfer_in'
                ? 'transfer-in'
                : movement.movementType === 'transfer_out'
                  ? 'transfer-out'
                  : 'adjust';
        movements.push({
          id: movement.movementId,
          branchId: movement.branchId,
          productId: movement.productId,
          type,
          qty: movement.quantity,
          at: Date.parse(movement.occurredAt),
        });
      }
      cursor = page.hasMore ? page.nextCursor : undefined;
    } while (cursor);
    return movements;
  },
  listStockMovementsPage: async (input: {
    branchId?: string;
    before?: string;
    limit?: number;
  }): Promise<{
    movements: StockMovement[];
    hasMore: boolean;
    nextCursor?: string;
  }> => {
    const page = await request<{
      movements: Array<{
        movementId: string;
        branchId: string;
        productId: string;
        movementType: string;
        quantity: number;
        occurredAt: string;
      }>;
      hasMore: boolean;
      nextCursor?: string;
    }>(
      withQuery('/dashboard/stock', {
        branchId: input.branchId,
        order: 'desc',
        before: input.before,
        limit: String(input.limit ?? 25),
      }),
    );
    return {
      movements: page.movements.map((movement) => {
        const type: StockMovement['type'] =
          movement.movementType === 'sale'
            ? 'sale'
            : movement.movementType === 'receive' ||
                movement.movementType === 'stock_in'
              ? 'receive'
              : movement.movementType === 'transfer_in'
                ? 'transfer-in'
                : movement.movementType === 'transfer_out'
                  ? 'transfer-out'
                  : 'adjust';
        return {
          id: movement.movementId,
          branchId: movement.branchId,
          productId: movement.productId,
          type,
          qty: movement.quantity,
          at: Date.parse(movement.occurredAt),
        };
      }),
      hasMore: page.hasMore,
      nextCursor: page.nextCursor,
    };
  },
  upsertBranch: async (
    branch: Branch,
    context?: Partial<DashboardApiContext>,
  ): Promise<Branch> => {
    await writeMasterEntity('branches', branch, context);
    return branch;
  },
  upsertCategory: async (
    category: Category,
    context?: Partial<DashboardApiContext>,
  ): Promise<Category> => {
    await writeMasterEntity('categories', category, context);
    return category;
  },
  /** Stores the photo only; it is linked once the product itself is saved. */
  uploadProductImage: async (
    productId: string,
    image: Blob,
  ): Promise<{ imageUrl: string }> => {
    assertMasterDataAccess('products', 'write');
    return request<{ imageUrl: string }>(
      withQuery('/dashboard/product-images', { productId }),
      {
        method: 'POST',
        headers: { 'content-type': image.type },
        body: image,
      },
    );
  },
  upsertProduct: async (
    product: Product,
    context?: Partial<DashboardApiContext>,
  ): Promise<Product> => {
    const reservedBarcode = product.units
      .map((unit) => unit.barcode?.trim() ?? '')
      .find((barcode) => barcode && isReservedProductBarcode(barcode));
    if (reservedBarcode) {
      throw new DashboardApiError({
        code: 'reserved_product_barcode',
        message: `บาร์โค้ด ${reservedBarcode} ใช้ไม่ได้ เนื่องจาก QTY ถูกสงวนไว้สำหรับคำสั่งจำนวนของ POS`,
        status: 400,
      });
    }
    const existingUnits = (
      await readMasterCollection<ApiProductUnitRecord>('product_units', context)
    ).filter((unit) => unit.productId === product.id);
    const existingBarcodes = (
      await readMasterCollection<ApiProductBarcodeRecord>(
        'product_barcodes',
        context,
      )
    ).filter((barcode) =>
      existingUnits.some((unit) => unit.id === barcode.productUnitId),
    );
    const baseUnit =
      (product.baseUnitId
        ? product.units.find((unit) => unit.id === product.baseUnitId)
        : undefined) ??
      product.units.find((unit) => unit.factor === 1) ??
      product.units[0];
    const baseUnitId = baseUnit?.id ?? `${product.id}:unit:1`;
    const nextUnitIds = new Set<string>();
    await writeMasterEntity(
      'products',
      {
        id: product.id,
        name: product.name,
        categoryId: product.categoryId,
        baseUnitId,
        active: product.active,
        minStock: product.minStock,
        cost: product.cost,
        // The form keeps '' for "no photo"; the Worker only accepts a URL.
        imageUrl: product.imageUrl || undefined,
      },
      context,
    );

    const barcodeWrites: Array<{
      id: string;
      productUnitId: string;
      barcode: string;
      active: true;
      type: 'retail';
      startsAt: null;
      endsAt: null;
      deletedAt: null;
    }> = [];
    const nextBarcodeIds = new Set<string>();

    for (const [index, unit] of product.units.entries()) {
      const unitId = resolveProductUnitId(
        product.id,
        unit.id,
        index,
        nextUnitIds,
      );
      nextUnitIds.add(unitId);
      await writeMasterEntity(
        'product_units',
        {
          id: unitId,
          productId: product.id,
          code: unit.code ?? deriveUnitCode(unit.unit, index),
          name: unit.unit,
          ratio: unit.factor,
          salePrice: unit.price,
          active: true,
        },
        context,
      );

      if (!unit.barcode?.trim()) {
        continue;
      }

      barcodeWrites.push({
        id: unit.barcodeId ?? `${unitId}:barcode`,
        productUnitId: unitId,
        barcode: unit.barcode.trim(),
        active: true,
        type: 'retail',
        startsAt: null,
        endsAt: null,
        deletedAt: null,
      });
      nextBarcodeIds.add(unit.barcodeId ?? `${unitId}:barcode`);
    }

    for (const barcode of existingBarcodes) {
      if (!nextBarcodeIds.has(barcode.id)) {
        await deleteMasterEntity('product_barcodes', barcode.id, context);
      }
    }

    for (const unit of existingUnits) {
      if (!nextUnitIds.has(unit.id)) {
        await deleteMasterEntity('product_units', unit.id, context);
      }
    }

    for (const barcode of barcodeWrites) {
      await writeMasterEntity('product_barcodes', barcode, context);
    }
    return product;
  },
  upsertUser: async (
    user: User,
    context?: Partial<DashboardApiContext>,
  ): Promise<User> => {
    await writeMasterEntity('users', user, context);
    return user;
  },
  deleteProduct: async (
    productId: string,
    context?: Partial<DashboardApiContext>,
  ): Promise<void> => {
    await deleteMasterEntity('products', productId, context);
  },
  upsertBranchPricing: async (
    productId: string,
    branchId: string,
    price: number | null,
    context?: Partial<DashboardApiContext>,
  ): Promise<BranchPricing[]> => {
    const [products, existingRows] = await Promise.all([
      readMasterCollection<ApiProductRecord>('products', context),
      readMasterCollection<ApiBranchPriceRecord>('branch_prices', context),
    ]);
    const product = products.find((row) => row.id === productId);
    if (!product?.baseUnitId) {
      throw new DashboardApiError('DASH-007', 'ไม่พบหน่วยหลักของสินค้า');
    }

    const recordId = `branch_price:${branchId}:${productId}:${product.baseUnitId}`;
    const updated = existingRows.filter(
      (row) =>
        !(
          row.productId === productId &&
          row.branchId === branchId &&
          row.unitId === product.baseUnitId
        ),
    );

    if (price === null) {
      const existingOverride = existingRows.find((row) => row.id === recordId);
      if (existingOverride) {
        await deleteMasterEntity('branch_prices', recordId, context);
      }
    } else {
      await writeMasterEntity(
        'branch_prices',
        {
          id: recordId,
          branchId,
          productId,
          unitId: product.baseUnitId,
          price,
        },
        context,
      );

      updated.push({
        id: recordId,
        branchId,
        productId,
        unitId: product.baseUnitId,
        price,
      });
    }

    const baseUnitByProductId = new Map(
      products.map((row) => [row.id, row.baseUnitId]),
    );
    const grouped = new Map<string, BranchPricing>();
    for (const row of updated) {
      const baseUnitId = baseUnitByProductId.get(row.productId);
      if (!baseUnitId || row.unitId !== baseUnitId) {
        continue;
      }
      const current = grouped.get(row.productId) ?? {
        productId: row.productId,
        prices: {},
      };
      current.prices[row.branchId] = row.price;
      grouped.set(row.productId, current);
    }

    return Array.from(grouped.values());
  },
  upsertPaymentMethod: async (
    paymentMethod: PaymentMethodConfig,
    context?: Partial<DashboardApiContext>,
  ): Promise<PaymentMethodConfig> => {
    await writeMasterEntity('payment_methods', paymentMethod, context);
    return paymentMethod;
  },
  saveBranchPaymentSettings: async (
    settings: BranchPaymentSettings,
    context?: Partial<DashboardApiContext>,
  ): Promise<BranchPaymentSettings> => {
    await writeMasterEntity('branch_payment_settings', settings, context);
    return settings;
  },
  upsertManualPaymentChannel: async (
    channel: ManualPaymentChannel,
    context?: Partial<DashboardApiContext>,
  ): Promise<ManualPaymentChannel> => {
    await writeMasterEntity('manual_payment_channels', channel, context);
    return channel;
  },
  upsertBranchManualPaymentChannel: async (
    assignment: BranchManualPaymentChannel,
    context?: Partial<DashboardApiContext>,
  ): Promise<BranchManualPaymentChannel> => {
    await writeMasterEntity(
      'branch_manual_payment_channels',
      assignment,
      context,
    );
    return assignment;
  },
  upsertPromotion: async (
    promotion: Promotion,
    context?: Partial<DashboardApiContext>,
  ): Promise<Promotion> => {
    // Map dashboard types to the shared promotion contract
    // before sending to the worker. See @pos/types Promotion.type.
    const apiPayload: SharedPromotion = mapDashboardPromotionToApi(promotion);
    await writeMasterEntity('promotions', apiPayload, context);
    return promotion;
  },
  saveSettings: async (
    settings: SystemSettings,
    context?: Partial<DashboardApiContext>,
  ): Promise<SystemSettings> => {
    assertMasterDataAccess('settings', 'write');
    const current = await readCurrentSettings(context);
    await writeMasterEntity(
      'settings',
      mapDashboardSettingsToApi(settings, current),
      context,
    );
    return settings;
  },
};
